import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Redirect, Link, useParams, useHistory, useLocation } from 'react-router-dom';
import { loadPaginatedFrontend, useFeathers } from '../../app/util';
import { useSelector } from 'react-redux';
import {
  CTAButton,
  SearchableSelectInput,
  CloseButton,
  HintTile,
  OfficialsSelectInput
} from '../../components';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { CircularProgress, Typography } from '@mui/material';
import moment from 'moment';

const priorityPartyKeys = ['D', 'R', 'N', 'I'];

const sortPartyOptions = (partyA, partyB) => {
  const partyAPriority = priorityPartyKeys.indexOf(partyA.value);
  const partyBPriority = priorityPartyKeys.indexOf(partyB.value);
  const partyAOrder = partyAPriority === -1 ? priorityPartyKeys.length : partyAPriority;
  const partyBOrder = partyBPriority === -1 ? priorityPartyKeys.length : partyBPriority;

  if (partyAOrder !== partyBOrder) return partyAOrder - partyBOrder;

  return partyA.label.localeCompare(partyB.label, undefined, { sensitivity: 'base' });
};

const CreateCandidate = ({
  raceData,
  allowedOfficialKeys = null,
  runoffPrecedentByOfficialKey = null,
  allowedOfficialKeysLoading = false,
  onClose,
  onSuccess,
  onError,
  onProcessingStart,
  runningMateForCandidateId,
}) => {
  const feathers = useFeathers()
  const history = useHistory();
  const location = useLocation();
  const election = useParams().key;
  const electionData = useSelector(state => state?.elections?.byKey[election])
  const isPartisan = electionData?.partisan;
  const isRunningMateMode = Boolean(runningMateForCandidateId);

  // Get error and form data from location state (if reopening after error)
  const locationError = location?.state?.error;
  const locationFormData = location?.state?.formData;

  // Official full is only for national races
  const [ officialFull, setOfficialFull ] = useState(null)
  const [ officialFullLoading, setOfficialFullLoading ] = useState(false);
  const [ nationalRaces, setNationalRaces ] = useState(null);
  const [ nationalCandidate, setNationalCandidate ] = useState(null);
  const [ allowCopyingOfFields, setAllowCopyingOfFields ] = useState(false);
  const [ copySourceCandidate, setCopySourceCandidate ] = useState(null);
  const [ selectingDifferentProfile, setSelectingDifferentProfile ] = useState(false);
  const [ allOfficialProfileOptions, setAllOfficialProfileOptions ] = useState([]);
  const [ candidateProfileOptions, setCandidateProfileOptions ] = useState([]);
  const [ candidateProfileOptionsLoading, setCandidateProfileOptionsLoading ] = useState(false);
  const [ partyOptions, setPartyOptions ] = useState([]);
  const [ partyOptionsLoading, setPartyOptionsLoading ] = useState(false);


  const mapRaceToOption = (race) => {
    if (!race) return null;
    return {
      text: race.officeName + (race?.party ? ` (${race?.party})` : ''),
      subText: race?.originalDistrict?.longName || race.district?.longName,
      isNationalRace: race.office?.district?.type === 'country',
      office: race.office,
      district: race.district,
      _id: race._id
    };
  };

  const formatWorkflowStatus = (status) => {
    if(!status) return 'Unpublished';
    return `${status}`
      .split('-')
      .map(part => part ? (part[0].toUpperCase() + part.slice(1)) : '')
      .join(' ');
  };

  const candidateName = (candidate) => {
    return candidate?.stagingDraft?.name || candidate?.name || 'Unknown candidate';
  };

  const candidatePhoto = (candidate) => {
    return candidate?.stagingDraft?.photoPathFace || candidate?.photoPathFace || null;
  };

  const candidatePublishStatus = (candidate) => {
    const workflowStatus = candidate?.workflow?.status;
    if(candidate?.synced && workflowStatus && workflowStatus !== 'draft') {
      return 'Published';
    }
    if(workflowStatus) {
      return formatWorkflowStatus(workflowStatus);
    }
    return 'Unpublished';
  };

  const candidateElectionName = (candidate) => {
    if(!candidate) return 'Unknown election';
    if(typeof candidate.election === 'string') return candidate.election;
    return candidate?.election?.name || candidate?.election?.key || 'Unknown election';
  };

  const getCopyableFields = (candidate) => {
    if(!candidate) return [];

    let fieldsToCopy = [];
    if(
      candidate?.references?.categories?.length > 0 &&
      candidate?.references?.checked
    ) fieldsToCopy.push('references');

    if(candidate?.photoPathFace || candidate?.stagingDraft?.photoPathFace) fieldsToCopy.push('photos');

    if(
      candidate?.bios?.length > 0 &&
      candidate?.bios?.every(bio => bio.complete)
    ) fieldsToCopy.push('bios');

    if(
      candidate?.issues?.length > 0 &&
      candidate?.issues?.every(issue => issue.complete)
    ) fieldsToCopy.push('issues');

    return fieldsToCopy;
  };

  const setCopySource = (candidate) => {
    const fieldsToCopy = getCopyableFields(candidate);
    setCopySourceCandidate(candidate || null);
    setAllowCopyingOfFields(fieldsToCopy);
    setFormInputs((formInputs) => ({
      ...formInputs,
      copyFieldsFromOfficial: fieldsToCopy
    }));
  };

  const searchCandidateProfiles = (searchText = '') => {
    const searchTextLower = (searchText || '').trim().toLowerCase();
    if(!searchTextLower) {
      setCandidateProfileOptions(allOfficialProfileOptions);
      return;
    }

    const filteredOptions = allOfficialProfileOptions.filter((opt) => (
      `${opt.electionName} ${opt.publishStatus} ${opt.name}`.toLowerCase().includes(searchTextLower)
    ));
    setCandidateProfileOptions(filteredOptions);
  };

  const loadCandidateProfilesForOfficial = async (officialKey) => {
    if(!feathers || !officialKey) return;
    setCandidateProfileOptionsLoading(true);
    try {
      const pageSize = 100;
      let skip = 0;
      let allCandidates = [];

      while(true) {
        const page = await feathers.getService('candidates').find({
          query: {
            official: officialKey,
            $sort: { createdAt: -1 },
            $limit: pageSize,
            $skip: skip
          }
        });
        const pageCandidates = page?.data || [];
        allCandidates = [...allCandidates, ...pageCandidates];
        if(pageCandidates.length < pageSize) break;
        skip += pageSize;
      }

      const options = allCandidates.map(candidate => ({
        _id: candidate._id,
        candidate,
        electionName: candidateElectionName(candidate),
        publishStatus: candidatePublishStatus(candidate),
        name: candidateName(candidate),
        photo: candidatePhoto(candidate),
        text: candidateName(candidate)
      }));

      setAllOfficialProfileOptions(options);
      setCandidateProfileOptions(options);
    } finally {
      setCandidateProfileOptionsLoading(false);
    }
  };

  const [ formInputs, setFormInputs ] = useState(() => {
    // Initialize with location form data if available (from error reopening)
    if (locationFormData) {
      return locationFormData;
    }

    const initialRaceOption = raceData ? mapRaceToOption(raceData) : null;

    return {
      race: initialRaceOption,
      official: null,
      ...(!isPartisan ? { party: null } : {}),
      copyFieldsFromOfficial: []
    };
  })
  const submittable = formInputs.race && formInputs.official && (nationalCandidate || (!isPartisan ? formInputs?.party : true)) && !officialFullLoading;

  const onChange = e => {
    const { name, value, isNew } = e.target;

    if(name === 'copyProfileCandidate') {
      if(value?.candidate) {
        setCopySource(value.candidate);
        setSelectingDifferentProfile(false);
      }
      return;
    }

    const update = {};
    update[name] = value;
    let newInputs = {
      ...formInputs,
      ...update
    };
    
    if (name === 'official' && !isNew && newInputs?.official?.key) {
      setSelectingDifferentProfile(false);
      setAllOfficialProfileOptions([]);
      setCandidateProfileOptions([]);
      setCopySourceCandidate(null);
      loadFullOfficialData(newInputs.official.key);
    }

    // See if we need to reset any formInputs
    if(!newInputs.official) {
      setError(null);
      setFormInputs(newInputs)
      setOfficialFull(null);
      setNationalRaces(null);
      setNationalCandidate(null);
      setAllowCopyingOfFields(false);
      setSelectingDifferentProfile(false);
      setAllOfficialProfileOptions([]);
      setCandidateProfileOptions([]);
      setCopySourceCandidate(null);
    } else {
      setFormInputs(newInputs)
    }
  }

  /* manage searching options for selection, based on search text */
  const [ optionsForField, setOptionsForField ] = useState({});
  const [ loadingForField, setLoadingForField ] = useState({});
  const searchForField = async (field, searchText) => {
    if(!feathers) return;

    const loadingUpdate = {};
    loadingUpdate[field] = true;
    setLoadingForField({ ...loadingForField, ...loadingUpdate });

    let options = [];
    if(field === 'official') {
      const officials = await feathers.getService('officials').find({ query:
        searchText ? { $search: searchText } : {}
      })
      options = officials.data.map(off => ({
        key: off.key,
        photo: off.recentCandidate?.stagingDraft?.photoPathFace || off.recentCandidate?.photoPathFace,
        name: off.recentCandidate?.stagingDraft?.name || off.recentCandidate?.name,
      }));
    }
    const updateForOptions = {};
    updateForOptions[field] = options;
    setOptionsForField({ ...optionsForField, ...updateForOptions })

    const loadingUpdate2 = {};
    loadingUpdate2[field] = false;
    setLoadingForField({ ...loadingForField, ...loadingUpdate2 });
  }

  const loadFullOfficialData = async (officialKey) => {
    setOfficialFullLoading(true);

    try {
      const fullOfficial = await feathers.getService('officials').get(officialKey);
      setOfficialFull(fullOfficial);
      const recentCandidateParty = fullOfficial?.recentCandidate?.party || null;
      if(!isPartisan) {
        setFormInputs((formInputs) => ({
          ...formInputs,
          party: recentCandidateParty
        }));
      }
      if(isRunningMateMode || !formInputs?.race?.isNationalRace) {
        setOfficialFullLoading(false);
        return;
      }

      // now, check if this official has any current national offices they're running for
      const potentialNationalOffices = (fullOfficial.candidates || []).filter(c => c.races?.length > 0);
      if(potentialNationalOffices.length === 0) {
        setOfficialFullLoading(false);
        return;
      }

      console.log('potentialNationalOffices', potentialNationalOffices)
      if(potentialNationalOffices.length > 1) {
        throw new Error(`This official is running for more than one national office. Dont know how to handle this case yet.`)
      }

      const nationalRaces = potentialNationalOffices[0].races;
      setNationalCandidate(potentialNationalOffices[0]);
      setNationalRaces(nationalRaces);
      setFormInputs((formInputs) => ({
        ...formInputs,
        party: potentialNationalOffices[0].party
      }))
      setOfficialFullLoading(false);
    } catch(error) {
      setError(error)
    }
  }


  const [ submitting, setSubmitting ] = useState(false);
  const [ successfulCreation, setSuccessfulCreation ] = useState(false);
  const [ error, setError ] = useState(locationError || null);

  useEffect(() => {
    let cancelled = false;

    const loadPartyOptions = async () => {
      if (!feathers || isPartisan !== false) {
        setPartyOptions([]);
        setPartyOptionsLoading(false);
        return;
      }

      setPartyOptionsLoading(true);
      try {
        const parties = await loadPaginatedFrontend('parties', { $sort: { key: 1 } }, feathers, 100);
        if (!cancelled) {
          setPartyOptions((parties || []).map((party) => ({
            value: party.key,
            label: party.name || party.key,
          })).sort(sortPartyOptions));
        }
      } catch (error) {
        console.error('Error fetching parties:', error);
        if (!cancelled) setPartyOptions([]);
      } finally {
        if (!cancelled) setPartyOptionsLoading(false);
      }
    };

    loadPartyOptions();

    return () => {
      cancelled = true;
    };
  }, [feathers, isPartisan]);
  
  // Update error when location state changes (e.g., when reopening after error)
  useEffect(() => {
    if (locationError) {
      setError(locationError);
    }
  }, [locationError]);
  const submit = async () => {
    if(submitting || !submittable) return;
    
    // Create temporary candidate object for optimistic display
    const tempId = `temp-${Date.now()}`;
    const candidateName = formInputs.official.isNew 
      ? formInputs.official.name 
      : (officialFull?.recentCandidate?.name || officialFull?.name || formInputs.official?.name || 'Loading...');
    const tempCandidate = {
      tempId,
      name: candidateName,
      party: formInputs.party || (nationalCandidate?.party) || null,
      _id: tempId,
      isProcessing: true
    };
    
    // Notify parent that we're starting to process
    if (onProcessingStart) {
      onProcessingStart(tempCandidate);
    }
    
    // Close modal immediately by navigating back (for better UX)
    if (onClose?.to) {
      // Navigate back immediately to close modal
      history.goBack();
    } else if (typeof onClose === 'function') {
      onClose();
    }
    
    // Create the candidate in background
    setSubmitting(true);
    setError(null);

    // Make API call in background
    try {
      let createdCandidate = null;
      
      if(nationalRaces && !isRunningMateMode) {
        if(!nationalCandidate) throw new Error('No national candidate selected')

        // in this case, just update the existing candidate with the new list of races 
        const newRaceIds = [ formInputs.race._id, ...nationalRaces.map(r => r._id) ];

        createdCandidate = await feathers.getService('candidates').patch(nationalCandidate._id, {
          races: newRaceIds
        })
        console.log('updated candidate', createdCandidate)
      } else {
        const selectedOfficialKey = formInputs?.official?.key;
        const runoffPrecedentForOfficial = selectedOfficialKey && runoffPrecedentByOfficialKey
          ? runoffPrecedentByOfficialKey[selectedOfficialKey]
          : null;

        const data = formInputs.official.isNew
          ?   // If we have a new official, pass in their name and use the $create flag
            {
              official: { $create: true },
              name: formInputs.official.name
            }
          :   // If this is an existing official, just provide their key
            {
              official: formInputs.official.key,
              copyFieldsFromOfficial: formInputs.copyFieldsFromOfficial,
              copyFromOfficialCandidate: copySourceCandidate?._id,
              ...(runoffPrecedentForOfficial ? { runoffPrecedent: runoffPrecedentForOfficial } : {})
            };

        createdCandidate = await feathers.getService('candidates').create({
          ...data,
          ...(formInputs.race.isNationalRace && !isRunningMateMode
            ? { races: [formInputs.race._id] }
            : { race: formInputs.race._id }),
          ...(!isPartisan ? { party: formInputs.party } : {}),
          ...(isRunningMateMode ? {
            runningMate: {
              candidate: runningMateForCandidateId,
              isPrimary: false
            }
          } : {})
        })
      }
      
      // Add tempId to created candidate for matching with processing list
      if (createdCandidate) {
        createdCandidate.tempId = tempId;
      }
      
      // Call success callback with the created candidate
      if(onSuccess) {
        onSuccess(createdCandidate)
      }
    } catch (error) {
      console.log(error)
      const errorObj = error.toJSON ? error.toJSON() : error;
      setError(errorObj)
      setSubmitting(false)
      
      // Add tempId to formInputs for matching with processing list
      const formInputsWithTempId = { ...formInputs, tempId };
      
      // Reopen modal with error via callback
      if(onError) {
        onError(errorObj, formInputsWithTempId)
      }
    }
  }

  useEffect(() => {
    if(officialFull) {
      // if the official has a recent candidate in the last year, consider allowing copying of fields
      const recentCandidate = officialFull.recentCandidate;
      if(recentCandidate && recentCandidate.createdAt && new Date(recentCandidate.createdAt) > new Date(Date.now() - 1000 * 60 * 60 * 24 * 365)) {
        setCopySource(recentCandidate);
        setSelectingDifferentProfile(false);

        return;
      }
    } 

    setAllowCopyingOfFields(false);
    setCopySourceCandidate(null);
    setFormInputs((formInputs) => ({
      ...formInputs,
      copyFieldsFromOfficial: []
    }));
  }, [officialFull])

  useEffect(() => {
    const selectedOfficialKey = formInputs?.official?.key;
    if(!selectedOfficialKey || !Array.isArray(allowedOfficialKeys)) return;
    if(allowedOfficialKeys.includes(selectedOfficialKey)) return;

    const newInputs = {
      ...formInputs,
      official: null
    };
    setError(null);
    setFormInputs(newInputs);
    setOfficialFull(null);
    setNationalRaces(null);
    setNationalCandidate(null);
    setAllowCopyingOfFields(false);
    setSelectingDifferentProfile(false);
    setAllOfficialProfileOptions([]);
    setCandidateProfileOptions([]);
    setCopySourceCandidate(null);
  }, [allowedOfficialKeys, formInputs]);

  return (
    <Wrapper>
      <CloseButton
        onClick={onClose}
        style={{
          position: 'absolute',
          height: '36px',
          right: '-20px',
          top: '-20px'
        }}
      />
      <Top style={officialFullLoading ? {pointerEvents: "none", opacity: 0.8 } : {}}>
        <h1>{isRunningMateMode ? 'New Running Mate' : 'New Candidate'}</h1>
        <Row>
          <div className='label'>Race</div>
          <SearchableSelectInput
            name='race'
            onChangeSearchText={(searchText) => searchForField('race', searchText)}
            value={formInputs.race}
            onChange={onChange}
            optionsLoading={loadingForField.race}
            options={optionsForField?.race || []}
            style={{ flexGrow: 1 }}
            disabled
          />
        </Row>
        {
          formInputs.race &&
          <Row>
            <div className='label'>Candidate</div>
            <OfficialsSelectInput
              key={Array.isArray(allowedOfficialKeys) ? allowedOfficialKeys.join('|') : 'all-officials'}
              onChange={onChange}
              value={formInputs.official}
              style={{ flexGrow: 1 }}
              office={formInputs.race?.office?.key || formInputs.race?.office}
              district={formInputs.race?.district?._id || formInputs.race?.district}
              allowedOfficialKeys={allowedOfficialKeys}
              disabled={allowedOfficialKeysLoading}
            />
          </Row>
        }
        {
          officialFullLoading &&
          <Row>
            <CircularProgress size={20} />
            <div className='label' style={{ marginLeft: '10px' }}>Loading...</div>
          </Row>
        }
        {
          nationalRaces &&
          <Row>
            <Typography variant='body1'>This candidate is also running for {nationalRaces[0].office} the following elections: {(nationalRaces || []).map(r => r.election).join(', ')}.</Typography>
          </Row>
        }
        {
          formInputs.race && formInputs.official && !isPartisan && !nationalRaces &&
          <Row>
            <div className='label'>Party</div>
            <TextField
              select
              name='party'
              onChange={onChange}
              style={{ flexGrow: 1 }}
              value={formInputs?.party || ''}
              disabled={partyOptionsLoading}
            >
              {partyOptionsLoading && <MenuItem disabled value=''>Loading parties...</MenuItem>}
              {!partyOptionsLoading && partyOptions.length === 0 && <MenuItem disabled value=''>No parties found</MenuItem>}
              {partyOptions.map((opt) => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
            </TextField>
          </Row>
        }
        {
          (
            selectingDifferentProfile ||
            copySourceCandidate ||
            (allowCopyingOfFields && allowCopyingOfFields.length > 0)
          ) &&
          <Row>
            <div className='label' style={{ alignSelf: 'flex-start' }}>Copy Fields</div>
            <div style={{ flexGrow: 1 }}>
              {
                selectingDifferentProfile
                ?
                  <SearchableSelectInput
                    name='copyProfileCandidate'
                    onChangeSearchText={searchCandidateProfiles}
                    value={null}
                    onChange={onChange}
                    optionsLoading={candidateProfileOptionsLoading}
                    options={candidateProfileOptions}
                    style={{ flexGrow: 1 }}
                    placeholder='Search candidate profiles for this official...'
                    searchOptionComponent={CandidateCopySourceOption}
                    allowReset={false}
                  />
                :
                  allowCopyingOfFields && allowCopyingOfFields.length > 0
                  ?
                    <CheckboxGrid>
                      {allowCopyingOfFields.map((field) => (
                        <CheckboxItem key={field}>
                          <input
                            type="checkbox"
                            id={`copy-${field}`}
                            checked={formInputs.copyFieldsFromOfficial.includes(field)}
                            onChange={(e) => {
                              const newCopyFields = e.target.checked
                                ? [...formInputs.copyFieldsFromOfficial, field]
                                : formInputs.copyFieldsFromOfficial.filter(f => f !== field);
                              setFormInputs({
                                ...formInputs,
                                copyFieldsFromOfficial: newCopyFields
                              });
                            }}
                          />
                          <label htmlFor={`copy-${field}`}>
                            {field === 'references' && 'References'}
                            {field === 'photos' && 'Photos'}
                            {field === 'bios' && 'Bios'}
                            {field === 'issues' && 'Issues'}
                          </label>
                        </CheckboxItem>
                      ))}
                    </CheckboxGrid>
                  :
                    <Typography variant='body2' style={{ opacity: 0.7 }}>
                      No complete fields available to copy from this profile.
                    </Typography>
              }
              {
                !selectingDifferentProfile && copySourceCandidate &&
                <Typography variant='body2' mt={1}>
                  This will be copied from {candidateName(copySourceCandidate)}'s profile, created {moment(copySourceCandidate?.createdAt).fromNow()}.{' '}
                  <InlineTextButton
                    type='button'
                    onClick={async () => {
                      if(!formInputs?.official?.key) return;
                      setSelectingDifferentProfile(true);
                      if(allOfficialProfileOptions.length === 0) {
                        await loadCandidateProfilesForOfficial(formInputs.official.key);
                      }
                    }}
                  >
                    Select a different profile
                  </InlineTextButton>
                  .
                </Typography>
              }
            </div>
          </Row>
        }
        {
          error &&
          <HintTile style={{ marginBottom: '20px' }}>
            {
              error.code === 409
              ?
                <ErrorText><b>Uh oh! </b>{formInputs?.official.name} is already running for <Link to={`/elections/${error.data.election?.[0]}/races/${error.data.race}`}>another race</Link> this election. If this is incorrect, please delete their record.</ErrorText>
              :
                <ErrorText><b>Uh oh! </b>Error creating this candidate: {error.message}</ErrorText>
            }
          </HintTile>
        }
        <CTAButton
          value={isRunningMateMode ? 'Create running mate' : (nationalRaces ? 'Add to race' : 'Create')}
          onClick={submit}
          disabled={!submittable || submitting}
        />
      </Top>
    </Wrapper>
  );

};

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 500px;
  min-height: 200px;
`
const Top = styled.div`
  display: flex;
  flex-direction: column;

  h1 {
    font-size: 28px;
    margin: 0 0 26px;
  }
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  margin-bottom: 28px;

  div.label {
    font-size: 18px;
    margin: 0 14px 0 0;
    opacity: 0.7;
    width: 100px;
    ${props => props.theme.font.normal}
    color: ${props => props.theme.colors.darkPurple};
  }
`;

const ErrorText = styled.div`
  ${props => props.theme.font.normal}
  color: ${props => props.theme.colors.darkPurple};
  b {
    ${props => props.theme.font.bold}
  }
`;

const CheckboxGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  flex-grow: 1;
`;

const CheckboxItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    accent-color: ${props => props.theme.colors.darkPurple};
  }

  label {
    ${props => props.theme.font.normal}
    color: ${props => props.theme.colors.darkPurple};
    font-size: 14px;
    cursor: pointer;
  }
`;

const InlineTextButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  margin: 0;
  color: ${props => props.theme.colors.darkPurple};
  text-decoration: underline;
  font-size: 12px;
  line-height: 1.2;

  &:hover {
    opacity: 0.8;
  }
`;

const CandidateCopySourceOption = ({
  electionName,
  publishStatus,
  name,
  photo
}) => {
  return (
    <CandidateCopySourceOptionInner>
      <div className='election'>{electionName}</div>
      <div className='status'>{publishStatus}</div>
      <div className='candidate'>
        {
          photo
          ? <img src={photo} alt='' />
          : <div className='avatar-fallback' />
        }
        <div>{name}</div>
      </div>
    </CandidateCopySourceOptionInner>
  );
};

const CandidateCopySourceOptionInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  .election {
    font-size: 14px;
    color: ${props => props.theme.palette.textPrimary};
  }

  .status {
    font-size: 12px;
    opacity: 0.7;
  }

  .candidate {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    opacity: 0.8;

    img {
      width: 20px;
      height: 20px;
      border-radius: 50%;
      object-fit: cover;
      background: #EEEEEE;
    }

    .avatar-fallback {
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: #EEEEEE;
      flex-shrink: 0;
    }
  }
`;

export default CreateCandidate;
